import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AdminSection } from "./admin";
import { TableShell } from "@/components/admin/AdminUI";
import { listAllSubscriptions } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/subscriptions")({
  component: SubscriptionsAdmin,
});

type Row = Awaited<ReturnType<typeof listAllSubscriptions>>[number];

const STATUS_TONE: Record<string, string> = {
  active: "text-emerald-400",
  trialing: "text-sky-400",
  past_due: "text-amber-400",
  canceled: "text-muted-foreground",
  incomplete: "text-amber-400",
  unpaid: "text-rose-400",
};

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

function SubscriptionsAdmin() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await listAllSubscriptions();
        setRows(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const active = rows.filter((r) =>
    ["active", "trialing", "past_due"].includes(r.status),
  ).length;
  const canceled = rows.filter((r) => r.status === "canceled").length;

  return (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Total" value={rows.length} />
        <Stat label="Active" value={active} tone="text-emerald-400" />
        <Stat label="Canceled" value={canceled} tone="text-muted-foreground" />
      </div>

      <AdminSection
        title="Memberships & subscriptions"
        description="All subscription records across sandbox and live. Cancellations revoke access immediately."
      >
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No subscriptions yet.</p>
        ) : (
          <TableShell
            headers={["Tier / Price", "Status", "Env", "Period", "Cancel@End", "Created"]}
            rows={rows.map((r) => (
              <tr key={r.id} className="border-t hairline">
                <td className="px-3 py-2 text-sm">
                  <div className="font-medium">{r.membership_tier ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">{r.price_id}</div>
                </td>
                <td className={`px-3 py-2 text-sm ${STATUS_TONE[r.status] ?? ""}`}>{r.status}</td>
                <td className="px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground">{r.environment}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {fmtDate(r.current_period_start)} → {fmtDate(r.current_period_end)}
                </td>
                <td className="px-3 py-2 text-xs">{r.cancel_at_period_end ? "yes" : "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          />
        )}
      </AdminSection>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border hairline p-6">
      <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className={`mt-3 font-serif text-4xl ${tone ?? "text-gold-luminous"}`}>{value}</p>
    </div>
  );
}
